import { useCallback, useState } from 'react';
import { useWebSearchToggleState } from '@/hooks/useWebSearchDefault';

interface PromptSuggestion {
  key: string;
  value: string;
}

interface UseCasePartnerStateReturn {
  // Input state
  userInput: string;
  setUserInput: (value: string) => void;
  composerQuote: string | null;
  setComposerQuote: (value: string | null) => void;

  // UI state
  showAllDocuments: boolean;
  setShowAllDocuments: (value: boolean) => void;
  webSearchEnabled: boolean;
  setWebSearchEnabled: (enabled: boolean) => void;
  resetWebSearchToDefault: () => void;
  copiedMessageIds: Set<string>;
  setCopiedMessageIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  isSidebarVisible: boolean;
  setIsSidebarVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isUploading: boolean;
  setIsUploading: (value: boolean) => void;
  isComposingNewChat: boolean;
  setIsComposingNewChat: (value: boolean) => void;

  // Dialog state
  showDocumentSelector: boolean;
  setShowDocumentSelector: (value: boolean) => void;
  isDeleteDialogOpen: boolean;
  chatToDelete: string | null;
  openDeleteDialog: (chatGuid: string) => void;
  closeDeleteDialog: () => void;
  showPromptLibrary: boolean;
  setShowPromptLibrary: (value: boolean) => void;

  // Prompt suggestions state
  promptSuggestions: PromptSuggestion[];
  setPromptSuggestions: (suggestions: PromptSuggestion[]) => void;
  showPromptSuggestions: boolean;
  setShowPromptSuggestions: (value: boolean) => void;
  hasShownInitialSuggestions: boolean;
  setHasShownInitialSuggestions: (value: boolean) => void;
}

/**
 * Custom hook for managing Case Partner UI state
 * Follows the useTaskDialogs.ts pattern with useCallback handlers
 */
export function useCasePartnerState(): UseCasePartnerStateReturn {
  // Input state
  const [userInput, setUserInput] = useState('');
  const [composerQuote, setComposerQuote] = useState<string | null>(null);

  // UI state
  const [showAllDocuments, setShowAllDocuments] = useState(false);
  const { webSearchEnabled, setWebSearchEnabled, resetWebSearchToDefault } = useWebSearchToggleState();
  const [copiedMessageIds, setCopiedMessageIds] = useState<Set<string>>(new Set());
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isComposingNewChat, setIsComposingNewChat] = useState(false);

  // Dialog state
  const [showDocumentSelector, setShowDocumentSelector] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<string | null>(null);
  const [showPromptLibrary, setShowPromptLibrary] = useState(false);

  // Prompt suggestions state
  const [promptSuggestions, setPromptSuggestions] = useState<PromptSuggestion[]>([]);
  const [showPromptSuggestions, setShowPromptSuggestions] = useState(false);
  const [hasShownInitialSuggestions, setHasShownInitialSuggestions] = useState(false);

  // Dialog handlers with useCallback (following useTaskDialogs pattern)
  const openDeleteDialog = useCallback((chatGuid: string) => {
    setTimeout(() => {
      setChatToDelete(chatGuid);
      setIsDeleteDialogOpen(true);
    }, 0);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setChatToDelete(null);
  }, []);

  return {
    // Input state
    userInput,
    setUserInput,
    composerQuote,
    setComposerQuote,

    // UI state
    showAllDocuments,
    setShowAllDocuments,
    webSearchEnabled,
    setWebSearchEnabled,
    resetWebSearchToDefault,
    copiedMessageIds,
    setCopiedMessageIds,
    isSidebarVisible,
    setIsSidebarVisible,
    isUploading,
    setIsUploading,
    isComposingNewChat,
    setIsComposingNewChat,

    // Dialog state
    showDocumentSelector,
    setShowDocumentSelector,
    isDeleteDialogOpen,
    chatToDelete,
    openDeleteDialog,
    closeDeleteDialog,
    showPromptLibrary,
    setShowPromptLibrary,

    // Prompt suggestions state
    promptSuggestions,
    setPromptSuggestions,
    showPromptSuggestions,
    setShowPromptSuggestions,
    hasShownInitialSuggestions,
    setHasShownInitialSuggestions,
  };
}
